/**
 * Pure helpers for referral section. Kept in a separate file for unit testing.
 */

export interface MessageWithMeta {
  id?: number;
  createdAt?: string | null;
  isQuestion: boolean;
  messageText: string;
  userDisplayName?: string;
  userId?: number;
}

export interface ReferralDataShape {
  messages?: MessageWithMeta[] | null;
  referredFromUserId?: number;
}

export function sortReferralMessages<T extends ReferralDataShape>(data: T): T {
  const messages = [...(data.messages ?? [])].sort((a, b) => {
    const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return timeA - timeB;
  });
  return { ...data, messages } as T;
}

export function formatReferralStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function getReferralErrorMessage(
  response: unknown,
  fallback: string,
  options?: { forCreate?: boolean },
): string {
  const err = response as { statusCode?: number; message?: string };
  if (options?.forCreate && err?.statusCode === 403) {
    return 'Only the assigned underwriter or case manager can create a referral.';
  }
  const msg = err?.message;
  return typeof msg === 'string' && msg.trim() !== '' ? msg : fallback;
}

/**
 * Formats the referral conversation for copy-paste (e.g. to cite in a new referral).
 * Format: "User Name: Message 1\nUser Name: Message 2"
 */
export function formatReferralConversationAsText(
  ref: ReferralDataShape,
): string {
  const sorted = sortReferralMessages(ref);
  return (sorted.messages ?? [])
    .map(msg => {
      const name = msg.userDisplayName?.trim() || 'Unknown';
      return `${name}: ${msg.messageText ?? ''}`;
    })
    .join('\n');
}
